'use strict'

const flow_ = require('lodash/flow')
const takeWhile_ = require('lodash/fp/takeWhile')

const { assign, bind } = require('./utils')
const { getRecordId } = require('./records')

// freshScope :: () -> Scope
const freshScope = () =>
  Object.assign(
    {},
    {
      records: [],
      numMatchingRecords: 0,
      numSeedRecords: 0,
      newRecordMarkers: []
    }
  )

// setScopeRecords :: [Record] -> Scope -> Scope
const setScopeRecords = records => assign({ records })

// updateNumMatchingRecords :: (Integer -> Integer) -> Scope -> Scope
const updateNumMatchingRecords = fn => scope =>
  Object.assign({}, scope, {
    numMatchingRecords: fn(scope.numMatchingRecords)
  })

// setNumMatchingRecords :: Integer -> Scope -> Scope
const setNumMatchingRecords = numMatchingRecords => scope =>
  Object.assign({}, scope, {
    numMatchingRecords: numMatchingRecords + newRecordMarkersOffset(scope, 0)
  })

// setNumSeedRecords :: Scope -> Scope
const setNumSeedRecords = scope =>
  Object.assign({}, scope, {
    numSeedRecords: Object.keys(scope.records).length
  })

// setSeedInScope :: QueryResult -> Scope -> Scope
const setSeedInScope = seed =>
  seed.matchWith({
    Empty: () => _ => _,
    Results: ({ items, totalCount, offset }) =>
      flow_(
        setScopeRecords(items.map(item => getRecordId(item))),
        setNumMatchingRecords(totalCount),
        setNumSeedRecords
      )
  })

// setNewRecordMarkers :: Integer -> Scope -> Scope
const setNewRecordMarkers = newRecordMarkers => assign({ newRecordMarkers })

// updateNewRecordMarkers :: (Integer -> Integer) -> Scope -> Scope
const updateNewRecordMarkers = fn => scope =>
  Object.assign({}, scope, { newRecordMarkers: fn(scope.newRecordMarkers) })

// newRecordMarkersOffset :: (Scope, Integer) -> Integer
const newRecordMarkersOffset = (scope, index) =>
  scope.newRecordMarkers.filter(marker => marker <= index).length

// storeResultsInScope :: QueryResults -> Scope -> Scope
const storeResultsInScope = queryResult =>
  queryResult.matchWith({
    Empty: () => _ => _,
    Results: ({ items, totalCount, offset }) =>
      flow_(
        setNumMatchingRecords(totalCount),
        items.length > 0 ? mergeRecords(offset, items) : _ => _
      )
  })

// mergeRecords :: (Integer, [Record], Options) -> Scope -> Scope
const mergeRecords = (
  startIndex,
  addedRecords,
  { overwrite = true, fixIndex = true } = {}
) => {
  const doMerge = scope => {
    const correctedStartIndex =
      startIndex + (fixIndex ? newRecordMarkersOffset(scope, startIndex) : 0)
    const padLength = Math.max(0, correctedStartIndex - scope.records.length)
    const tailIndex =
      correctedStartIndex + (overwrite ? addedRecords.length : 0)

    return scope.records
      .slice(0, correctedStartIndex)
      .concat(Array(padLength))
      .concat(addedRecords.map(record => getRecordId(record)))
      .concat(scope.records.slice(tailIndex))
  }

  return bind(doMerge, setScopeRecords)
}

// overwriteRecordAtIndex :: (Integer, Record) -> Scope -> Scope
const overwriteRecordAtIndex = (at, record) =>
  mergeRecords(at, [record], { fixIndex: false })

// insertRecordAtIndex :: (Integer, Record) -> Scope -> Scope
const insertRecordAtIndex = (at, record) =>
  mergeRecords(at, [record], { overwrite: false, fixIndex: false })

// removeRecordById :: RecordId -> Scope -> Scope
const removeRecordById = id =>
  bind(
    scope => scope.records.filter(recordId => recordId !== id),
    setScopeRecords
  )

// recordIndexById :: (RecordId, Scope) -> Integer
const recordIndexById = (id, scope) => scope.records.indexOf(id)

// scopeHasRecord :: RecordId -> Scope -> Bool
const scopeHasRecord = id => scope => scope.records.includes(id)

// countMissingRecords :: (Integer, Integer, [Record]) -> Integer
const countMissingRecords = (records, from, to) =>
  takeWhile_(_ => typeof _ !== 'string', records.slice(from, to)).length

// collectMissingRanges :: (Scope, Integer, Integer, [(Integer, Integer)]) -> [(Integer, Integer)]
const collectMissingRanges = (scope, from, to, acc = []) => {
  const addMissingRange = (acc, rangeStart, rangeEnd) => {
    const indexCorrection = newRecordMarkersOffset(scope, from)
    const correctedFrom = rangeStart - indexCorrection
    const correctedTo = rangeEnd - indexCorrection
    return acc.concat([[correctedFrom, correctedTo - correctedFrom]])
  }

  if (from >= scope.records.length) {
    return addMissingRange(acc, from, to)
  } else if (from >= to) {
    return acc
  }

  const countNextMissingRecords = countMissingRecords(scope.records, from, to)
  if (countNextMissingRecords) {
    return collectMissingRanges(
      scope,
      from + countNextMissingRecords,
      to,
      addMissingRange(acc, from, from + countNextMissingRecords)
    )
  } else {
    return collectMissingRanges(scope, from + 1, to, acc)
  }
}

module.exports.collectMissingRanges = collectMissingRanges
module.exports.freshScope = freshScope
module.exports.insertRecordAtIndex = insertRecordAtIndex
module.exports.newRecordMarkersOffset = newRecordMarkersOffset
module.exports.overwriteRecordAtIndex = overwriteRecordAtIndex
module.exports.recordIndexById = recordIndexById
module.exports.removeRecordById = removeRecordById
module.exports.scopeHasRecord = scopeHasRecord
module.exports.setNewRecordMarkers = setNewRecordMarkers
module.exports.setSeedInScope = setSeedInScope
module.exports.storeResultsInScope = storeResultsInScope
module.exports.updateNewRecordMarkers = updateNewRecordMarkers
module.exports.updateNumMatchingRecords = updateNumMatchingRecords



// WEBPACK FOOTER //
// ./recordStore/scopes.js